import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { apiGet, apiPost, apiDelete, API_BASE, formatDate, formatDateTime, formatLKR, formatNumber, formatPct } from "@/lib/api";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, RefreshCcw, ArrowLeft, FileText, Upload, Loader2, CloudDownload, LineChart as LineChartIcon, TrendingUp, TrendingDown, ArrowRight, Eye, Download, GitCompare, Info, Trash2, Pencil, AlertTriangle } from "lucide-react";
import SignalBadge from "@/components/common/SignalBadge";
import ValuationBadge from "@/components/common/ValuationBadge";
import DataConfidenceDot from "@/components/common/DataConfidenceDot";
import FundamentalsSourceBadge from "@/components/common/FundamentalsSourceBadge";
import SectorVerdictPill from "@/components/common/SectorVerdictPill";
import ScoreGauge from "@/components/common/ScoreGauge";
import ShareTypePill from "@/components/stocks/ShareTypePill";
import ShareClassComparisonCard from "@/components/stocks/ShareClassComparisonCard";
import MarketContextCard from "@/components/stock-detail/MarketContextCard";
import StockAIChatTab from "@/components/stock-detail/StockAIChatTab";
import ReportAnalysisStatusPanel from "@/components/stock-detail/ReportAnalysisStatusPanel";
import { DataSourceNote, AiAnalysisWarning, VisionOcrWarning } from "@/components/legal/DataNotes";
import NewsTab from "@/components/stocks/NewsTab";
import DividendTimeline from "@/components/dividends/DividendTimeline";
import WatchlistStarButton from "@/components/common/WatchlistStarButton";
import EditFundamentalsDialog from "@/components/stocks/EditFundamentalsDialog";
import EpsHistoryChart from "@/components/stocks/EpsHistoryChart";
import InvestmentRationalePanel from "@/components/stocks/InvestmentRationalePanel";
import TechnicalAnalysisCard from "@/components/stocks/TechnicalAnalysisCard";
import IntrinsicValueSection from "@/components/stocks/IntrinsicValueSection";
import TargetPriceCard from "@/components/stocks/TargetPriceCard";
import DeepAnalysisSection from "@/components/stocks/DeepAnalysisSection";
import DeepAnalysisErrorBoundary from "@/components/stocks/DeepAnalysisErrorBoundary";
import FiveLayerBreakdownPanel from "@/components/stocks/FiveLayerBreakdownPanel";
import ReportAnalysisModal from "@/components/stocks/ReportAnalysisModal";
import ReportUploadForm from "@/components/common/ReportUploadForm";
import ReportAnalysisBanner from "@/components/common/ReportAnalysisBanner";
import SeoHead from "@/components/common/SeoHead";
import useSmartPoll from "@/hooks/useSmartPoll";
import { useAuth } from "@/lib/auth";
import {
  ComposedChart, Line, Bar, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
  LineChart, ReferenceLine,
} from "recharts";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const RANGE_OPTIONS = [
  { value: "7", label: "7D", min: 7 },
  { value: "30", label: "30D", min: 14 },
  { value: "90", label: "90D", min: 30 },
  { value: "180", label: "180D", min: 60 },
  { value: "365", label: "1Y", min: 120 },
];

// IPO redirect card shown when `/stocks/:companyCode` is requested
// for a symbol whose analysis lives under `/ipo/:symbol` instead
// (e.g. pre-listing open subscription like JXG). Renders the IPO
// signal prominently and routes users to the full IPO detail page.
const IpoRedirectCard = ({ ipo, companyCode, onBack }) => {
  const rec = ipo?.ipo_recommendation || {};
  const det = ipo?.ipo_details || {};
  const status = (det.ipo_status || "").toLowerCase();
  const signal = rec.signal || null;
  const signalClass =
    signal === "STRONG SUBSCRIBE" || signal === "SUBSCRIBE"
      ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"
      : signal === "WATCHLIST"
        ? "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"
        : signal === "AVOID"
          ? "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30"
          : "bg-muted text-muted-foreground border-border";
  const statusLabel =
    status === "open"
      ? "Subscription open"
      : status === "upcoming"
        ? "Upcoming IPO"
        : status === "closed"
          ? "Subscription closed — awaiting listing"
          : "IPO";
  const subOpens = det.subscription_opens ? new Date(det.subscription_opens) : null;
  const subCloses = det.subscription_closes ? new Date(det.subscription_closes) : null;
  const listDate = det.listing_date ? new Date(det.listing_date) : null;
  const fmtDate = (d) =>
    d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  return (
    <div className="space-y-6" data-testid="stock-detail-ipo-redirect">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="text-muted-foreground -ml-2 hover:text-foreground"
        data-testid="stock-detail-ipo-redirect-back"
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>
      <Card className="border bg-card">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wider bg-[hsl(var(--info))]/10 border-[hsl(var(--info))]/40 text-[hsl(var(--info))]"
            >
              IPO Analysis
            </Badge>
            <Badge variant="secondary" className="text-[10px]">{statusLabel}</Badge>
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold" data-testid="ipo-redirect-company-name">
              {ipo.company_name || companyCode}
            </h1>
            <div className="text-sm text-muted-foreground mt-1">
              <span className="font-mono">{ipo.symbol}</span>
              {ipo.sector ? <> · {ipo.sector}</> : null}
            </div>
          </div>
          <p className="text-sm text-foreground/90 leading-relaxed">
            This company is a new primary issue on the Colombo Stock Exchange and has not been fully
            listed long enough for a historical fundamentals-based composite score to be meaningful.
            The dedicated IPO analysis page carries the prospectus-driven recommendation, valuation
            at IPO price, and 5-year financial history.
          </p>
          {signal && (
            <div
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold ${signalClass}`}
              data-testid={`ipo-redirect-signal-${String(signal).replace(/\s+/g, "-").toLowerCase()}`}
            >
              <Sparkles className="h-4 w-4" />
              {signal}
              {rec.conviction ? (
                <span className="text-xs font-normal opacity-80">· {rec.conviction} conviction</span>
              ) : null}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">IPO price</div>
              <div className="font-semibold">
                {det.ipo_price != null ? `Rs. ${Number(det.ipo_price).toFixed(2)}` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Subscription opens</div>
              <div className="font-semibold text-xs">{fmtDate(subOpens)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Subscription closes</div>
              <div className="font-semibold text-xs">{fmtDate(subCloses)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Listing date</div>
              <div className="font-semibold text-xs">{fmtDate(listDate)}</div>
            </div>
          </div>
          <div className="pt-2 flex flex-wrap gap-2">
            <Button asChild size="sm" className="gap-2" data-testid="ipo-redirect-view-analysis">
              <Link to={`/ipo/${ipo.symbol}`}>
                View IPO Analysis <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline" className="gap-2" data-testid="ipo-redirect-view-list">
              <Link to="/ipo">
                All IPOs
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const StockDetailPage = () => {
  const { companyCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const fromState = location.state?.from;
  const backLabel = fromState?.label || "Stocks";
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSymbol, setActiveSymbol] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [range, setRange] = useState("30");
  const [prices, setPrices] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [compositeScore, setCompositeScore] = useState(null);
  const [sectorCompare, setSectorCompare] = useState(null);
  const [reportAnalysis, setReportAnalysis] = useState(null); // { open, report_id, data, loading }
  // IPO redirect detection. If `/stocks/:companyCode` is requested but
  // the symbol corresponds to a pre-listing or very recently listed
  // IPO, the legacy fundamentals-driven UI is misleading (composite
  // score collapses to ~20 / AVOID because BVPS/ROE are missing). We
  // fetch `/ipo/list` in parallel with the company bundle and either:
  //   (a) render an IPO redirect card when the company is not in our
  //       securities universe yet (JXG pre-listing); or
  //   (b) render a thin banner above the normal detail UI for recently
  //       listed IPOs so users can jump to the IPO analysis page.
  const [ipoInfo, setIpoInfo] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiGet(`/companies/${companyCode}/full`)
      .then((b) => {
        setBundle(b);
        const def =
          b.securities.find((s) => s.security.share_type === "voting") || b.securities[0];
        if (def) setActiveSymbol(def.security.cse_symbol);
      })
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [companyCode]);

  // Fetch the IPO universe once on mount so we can detect when the
  // requested code is actually an IPO symbol.
  useEffect(() => {
    if (!companyCode) return;
    let cancelled = false;
    apiGet("/ipo/list")
      .then((buckets) => {
        if (cancelled) return;
        const all = [
          ...(buckets?.upcoming || []),
          ...(buckets?.open || []),
          ...(buckets?.recently_listed || []),
          ...(buckets?.historical || []),
          ...(buckets?.pending_prospectus || []),
        ];
        const needle = String(companyCode || "").toUpperCase();
        const match = all.find((r) => {
          const sym = String(r.symbol || "").toUpperCase();
          const prefix = sym.split(".")[0];
          return sym === needle || prefix === needle;
        });
        if (match) setIpoInfo(match);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [companyCode]);

  useEffect(() => {
    if (!activeSymbol) return;
    apiGet(`/prices/${activeSymbol}?days=${range}`).then(setPrices).catch(() => setPrices([]));
  }, [activeSymbol, range]);

  // Score history for the active security (Phase 5 — Step 7)
  useEffect(() => {
    if (!activeSymbol) return;
    apiGet(`/composite-scores/${activeSymbol}/history?limit=60`)
      .then((rows) => setScoreHistory(Array.isArray(rows) ? rows : []))
      .catch(() => setScoreHistory([]));
  }, [activeSymbol]);

  // Latest composite score doc — Phase 6 layer breakdown + intrinsic values
  useEffect(() => {
    if (!activeSymbol) return;
    apiGet(`/composite-scores/${activeSymbol}`)
      .then(setCompositeScore)
      .catch(() => setCompositeScore(null));
  }, [activeSymbol]);

  // Sector comparison for the active security (Issue 8 — Sector vs stock medians)
  useEffect(() => {
    if (!activeSymbol) return;
    apiGet(`/sector-comparison/${activeSymbol}`)
      .then(setSectorCompare)
      .catch(() => setSectorCompare(null));
  }, [activeSymbol]);

  const openReportAnalysis = (report) => {
    setReportAnalysis({ open: true, report_id: report.report_id, data: null, loading: true });
    apiGet(`/reports/${report.report_id}/analysis`)
      .then((data) => setReportAnalysis({ open: true, report_id: report.report_id, data, loading: false }))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load analysis");
        setReportAnalysis(null);
      });
  };
  const closeReportAnalysis = () => setReportAnalysis(null);

  // Claude analysis trigger state (Issue #12)
  const [analysingIds, setAnalysingIds] = useState(() => new Set());

  // Single-stock composite-score recompute (admin-only — see
  // POST /api/admin/recompute-scores/{symbol}). Lets the admin see the
  // scoring impact of newly uploaded reports / refreshed fundamentals
  // on THIS stock without running the 284-symbol nightly sweep.
  const [recomputing, setRecomputing] = useState(false);
  const recomputeScore = async () => {
    const sym = activeSec?.security?.cse_symbol || activeSymbol;
    if (!sym) return;
    setRecomputing(true);
    try {
      const res = await apiPost(`/admin/recompute-scores/${sym}`);
      if (!res?.ok) {
        toast.info(res?.message || "Not enough data to compute a score yet.");
        return;
      }
      const prev = res.previous || {};
      const curr = res.current || {};
      const hasPrev = prev.final_score != null || prev.signal;
      const fmt = (v) => (v == null ? "—" : v);
      const desc = hasPrev
        ? `Score: ${fmt(prev.final_score)} → ${fmt(curr.final_score)} · Signal: ${fmt(prev.signal)} → ${fmt(curr.signal)}`
        : `Score: ${fmt(curr.final_score)} · Signal: ${fmt(curr.signal)}`;
      if (res.changed) {
        toast.success(`${sym} · Recalculated`, { description: desc, duration: 6000 });
      } else {
        toast.info(`${sym} · Score unchanged`, { description: desc, duration: 5000 });
      }
      // Refresh the visible breakdown panel with the new numbers.
      try {
        const fresh = await apiGet(`/composite-scores/${sym}`);
        setCompositeScore(fresh);
      } catch { /* keep last-good on transient read error */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  const analyseReport = async (report) => {
    setAnalysingIds((prev) => {
      const next = new Set(prev);
      next.add(report.report_id);
      return next;
    });
    try {
      toast.info(`Analysing ${report.title || report.report_id}\u2026`);
      await apiPost(`/reports/${report.report_id}/analyse`, {});
      // Prompt admin to recalculate the composite score so the impact
      // of this report's fundamentals is reflected immediately (instead
      // of waiting for the nightly 284-symbol sweep). One-tap action on
      // the toast calls our local `recomputeScore` handler.
      toast.success("Analysis complete", {
        description: "Recalculate the composite score to reflect the new numbers.",
        action: { label: "Recalculate", onClick: () => recomputeScore() },
        duration: 8000,
      });
      await loadReports();
      // Auto-open the new analysis
      openReportAnalysis(report);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalysingIds((prev) => {
        const next = new Set(prev);
        next.delete(report.report_id);
        return next;
      });
    }
  };

  // Fix 1 — Retry a failed analysis. Uses force=true so the backend
  // bypasses the analysis-complete short-circuit and calls Claude
  // again. Combined with the new transient-error retry logic in
  // _run_report_analysis, most LLM 502 / 503 spikes will heal on their
  // own; this gives the admin a manual "try again" path for edge cases.
  const retryReportAnalysis = async (report) => {
    setAnalysingIds((prev) => {
      const next = new Set(prev);
      next.add(report.report_id);
      return next;
    });
    try {
      toast.info(`Retrying analysis for ${report.title || report.report_period || report.report_id}\u2026`);
      await apiPost(`/reports/${report.report_id}/analyse?force=true`, {});
      toast.success("Retry queued — Claude is re-analysing the report");
      await loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setAnalysingIds((prev) => {
        const next = new Set(prev);
        next.delete(report.report_id);
        return next;
      });
    }
  };

  // NOTE: the old `analyseAllReports` / `bulkAnalysing` pair was
  // removed 2026-05-02 in favour of the unified `runBulkAction(mode)`
  // wired to the 3-button admin control in the Reports tab. Kept this
  // anchor comment so future grepping finds the rename.

  // Re-enable a report that was previously `skipped` (admin-cancelled
  // queue entry) or `discovered` (parked by the cron, awaiting admin
  // promotion). Calls POST /api/admin/reports/{report_id}/unskip with
  // either target_status="pending" (queue immediately) or
  // target_status="discovered" (park).
  const unskipReport = async (report, targetStatus = "pending") => {
    setAnalysingIds((prev) => {
      const next = new Set(prev);
      next.add(report.report_id);
      return next;
    });
    const wasSkipped = (report.analysis_status || "").toLowerCase() === "skipped";
    try {
      const resp = await apiPost(
        `/admin/reports/${report.report_id}/unskip?target_status=${encodeURIComponent(targetStatus)}`,
        {},
      );
      if (targetStatus === "pending") {
        toast.success(
          wasSkipped ? "Report re-queued for analysis" : "Report queued for AI analysis",
          {
            description: resp?.data?.noop
              ? "Already in the queue — Claude will pick it up shortly."
              : "Claude will pick it up in the next batch (~$0.30 of API credits).",
          },
        );
      } else {
        toast.success("Report parked as discovered");
      }
      await loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to enqueue report");
    } finally {
      setAnalysingIds((prev) => {
        const next = new Set(prev);
        next.delete(report.report_id);
        return next;
      });
    }
  };

  // Bulk re-enable — flip every `skipped` row for this stock back to
  // `pending` in a single backend call. Used to turn a stock full of
  // admin-skipped rows into a ready-to-analyse batch so the admin can
  // immediately follow with "Analyse Downloaded Only".
  const [unskipAllBusy, setUnskipAllBusy] = useState(false);
  // Feature 2 — broker-style PDF research report download. Single-flight
  // with `downloadingPdf` so the user can't double-click and trigger two
  // backend renders at once.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadResearchReport = async (cseSymbol, companyName) => {
    if (!cseSymbol || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("cse_auth_token");
      const res = await fetch(
        `${backendUrl}/api/stocks/${encodeURIComponent(cseSymbol)}/report/pdf`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        let msg = `Failed to generate report (HTTP ${res.status})`;
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
        } catch (_) {
          /* not JSON */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `${cseSymbol}_research_report_${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Research report downloaded", {
        description: `${companyName || cseSymbol} \u2022 6-page PDF`,
      });
    } catch (e) {
      toast.error(e?.message || "Could not generate PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };
  const unskipAllForCompany = async () => {
    setUnskipAllBusy(true);
    try {
      const res = await apiPost(
        `/admin/reports/company/${encodeURIComponent(companyCode)}/unskip-all?target_status=pending`,
        {},
      );
      const n = res?.flipped ?? 0;
      if (n === 0) {
        toast.info("No skipped reports to re-enable");
      } else {
        toast.success(`Re-enabled ${n} report${n === 1 ? "" : "s"}`, {
          description: "Click \u2018Analyse Downloaded Only\u2019 to run Claude on them.",
        });
      }
      await loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk re-enable failed");
    } finally {
      setUnskipAllBusy(false);
    }
  };

  // Fix 5 — delete a report (admin only). Confirmation dialog is
  // rendered inline below; this just performs the network call once
  // the user has confirmed.
  const [deleteTarget, setDeleteTarget] = useState(null); // {report_id, title}
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/companies/${companyCode}/reports/${deleteTarget.report_id}`);
      toast.success("Report deleted successfully");
      setDeleteTarget(null);
      await loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const loadReports = async ({ background = false } = {}) => {
    if (!background) setReportsLoading(true);
    try {
      const r = await apiGet(`/reports/company/${companyCode}`);
      const next = Array.isArray(r) ? r : [];
      // Diff-aware update — only call setReports when something
      // actually changed. Uses JSON.stringify on the fields we care
      // about; safe because the payload is small (≤ 200 rows). When
      // nothing changed we return `prev` from the updater so React
      // bails out of re-rendering entirely (this is what fixes the
      // 10-second visible flicker).
      setReports((prev) => {
        if (!Array.isArray(prev) || prev.length !== next.length) return next;
        const fingerprint = (r) => `${r.report_id}|${r.analysis_status || ""}|${r.is_analysed ? 1 : 0}|${r.analysis_complete ? 1 : 0}|${r.analysis_error || ""}|${r.report_period || ""}|${r.report_type || ""}|${r.size_bytes || 0}`;
        const prevSig = prev.map(fingerprint).join("##");
        const nextSig = next.map(fingerprint).join("##");
        return prevSig === nextSig ? prev : next;
      });
    } catch { /* keep last-good state on error */ } finally {
      if (!background) setReportsLoading(false);
    }
  };
  useEffect(() => { loadReports(); /* eslint-disable-next-line */ }, [companyCode]);

  // Fix 3 (round 2) — smart polling. Only active when the Reports
  // tab is the visible tab AND at least one row is still in
  // analysing|pending state. Stops completely once everything reaches
  // a terminal state. Polls every 15 s with a 30 s failure backoff.
  // Background calls don't toggle the loading skeleton, eliminating
  // the visible "page rebuild" flicker.
  const reportsTabActive = activeTab === "reports";
  const hasInFlightAnalysis = (reports || []).some((r) => {
    const s = (r.analysis_status || "").toLowerCase();
    return s === "analysing" || s === "pending";
  });
  useSmartPoll({
    fetch: () => loadReports({ background: true }),
    shouldPoll: () => hasInFlightAnalysis,
    intervalMs: 15_000,
    errorBackoffMs: 30_000,
    enabled: reportsTabActive && hasInFlightAnalysis,
  });

  // Admin-only: pull queue positions for the active security so each
  // "Downloaded · Not analysed" row can show "📥 Queued — position 3
  // of 12". Polls lazily (only when the reports tab is open and the
  // admin is viewing) and stops once the queue drains for this symbol.
  const [queueMap, setQueueMap] = useState({}); // report_id → { position, queue_length }
  // Controlled flag for the fundamentals-verify dialog opened from the
  // report row's "Verify Data" action (re-uses the same dialog the
  // Fundamentals tab already renders, so admins get a single source of
  // truth for numeric overrides).
  const [verifyReportOpen, setVerifyReportOpen] = useState(false);
  useEffect(() => {
    if (!isAdmin || !reportsTabActive || !activeSymbol) return undefined;
    let cancelled = false;
    let timeout = null;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await apiGet(
          `/admin/reports/queue-position/${encodeURIComponent(activeSymbol)}`,
        );
        if (cancelled) return;
        const map = {};
        for (const p of r.positions || []) {
          if (p.report_id) {
            map[p.report_id] = {
              position: p.position,
              queue_length: r.queue_length,
              is_prioritised: p.is_prioritised,
            };
          }
        }
        setQueueMap(map);
        // Keep polling if this symbol still has queued reports.
        if ((r.positions || []).length > 0 && !cancelled) {
          timeout = setTimeout(tick, 15000);
        }
      } catch {
        // Silent — endpoint is admin-only and optional.
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [isAdmin, reportsTabActive, activeSymbol]);

  const refreshAi = async () => {
    setRefreshing(true);
    try {
      const updated = await apiPost(`/ai-analysis/${companyCode}/refresh`);
      setBundle((b) => ({ ...b, ai_analysis: updated }));
      toast.success("AI analysis refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  // ------------- 3-button bulk actions (per-stock) -------------
  // Replaces the old single "Auto-download from CSE" + "Analyse All"
  // buttons with the admin-requested 3 granular options:
  //   📥 Download Missing Only        → CSE fetch, NO Claude analysis
  //   ✨ Analyse Downloaded Only      → Claude ONLY on pending local reports
  //   📥✨ Download & Analyse All     → CSE fetch + Claude on the new PDFs
  //
  // Separates network cost (cheap) from LLM cost (expensive) so admins
  // can opt into each half of the pipeline independently.
  const [bulkBusy, setBulkBusy] = useState(null); // "download" | "analyse" | "both" | null
  const runBulkAction = async (mode) => {
    // mode: 'download' | 'analyse' | 'both'
    if (!companyCode) return;
    const actionMap = { download: "download", analyse: "analyse", both: "download_and_analyse" };
    const action = actionMap[mode];
    setBulkBusy(mode);
    try {
      const res = await apiPost(`/admin/reports/bulk-action`, {
        action,
        company_codes: [companyCode],
        limit_per_company: 10,
        analysis_limit: 40,
      });
      const downloaded = res?.downloaded ?? 0;
      const skipped = res?.skipped_existing ?? 0;
      const queued = res?.analysis_queued ?? 0;
      if (mode === "download") {
        if (downloaded > 0) {
          toast.success(`Downloaded ${downloaded} new report${downloaded === 1 ? "" : "s"}`, {
            description: skipped ? `${skipped} already on file. Analysis not triggered.` : "Analysis not triggered — click ‘Analyse Downloaded Only’ to run Claude.",
          });
        } else {
          toast.info("No new reports found on cse.lk", {
            description: skipped ? `${skipped} already on file.` : "All caught up.",
          });
        }
      } else if (mode === "analyse") {
        if (queued > 0) {
          toast.success(`Queued ${queued} report${queued === 1 ? "" : "s"} for Claude analysis`, {
            description: "Running in the background. Refresh in ~1–2 min.",
            duration: 6000,
          });
        } else {
          toast.info("No pending reports to analyse", {
            description: "Every report for this stock is already analysed.",
          });
        }
      } else if (mode === "both") {
        if (downloaded === 0 && queued === 0) {
          toast.info("No new reports found on cse.lk", {
            description: skipped ? `${skipped} already on file.` : "All caught up.",
          });
        } else {
          toast.success(
            `Downloaded ${downloaded} · Analysing ${queued}`,
            {
              description: "Claude is processing the new reports in the background.",
              duration: 6000,
            }
          );
        }
      }
      // Refresh the reports list — downloads are synchronous, analysis
      // is async so we poll again after a short delay.
      loadReports();
      if (mode !== "download") {
        setTimeout(loadReports, 5000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk ${mode} failed`);
    } finally {
      setBulkBusy(null);
    }
  };

  // Reports tab — upload state has moved into the unified
  // <ReportUploadForm /> component. This page only needs to know when
  // an upload completes so it can refresh the list (handled via the
  // onUploaded callback below).

  const ai = bundle?.ai_analysis;
  const activeSec = useMemo(
    () => bundle?.securities.find((s) => s.security.cse_symbol === activeSymbol),
    [bundle, activeSymbol]
  );
  const aiForActive = useMemo(() => {
    if (!ai || !activeSec) return null;
    return ai.securities_analysis?.find(
      (s) => s.cse_symbol === activeSec.security.cse_symbol
    );
  }, [ai, activeSec]);

  if (loading) return <LoadingState message="Loading company…" />;
  // When /stocks/:companyCode can't find the company but this symbol
  // is a known IPO (e.g. JXG pre-listing), render an IPO redirect
  // card instead of the generic "Not found" error. This avoids
  // sending users into a dead-end and steers them to the correct
  // /ipo/:symbol page where the actual analysis lives.
  if ((error || !bundle) && ipoInfo) {
    return <IpoRedirectCard ipo={ipoInfo} companyCode={companyCode} onBack={() => navigate(-1)} />;
  }
  if (error || !bundle) return <ErrorState message={error || "Not found"} />;

  const company = bundle.company;
  const fund = activeSec?.latest_fundamentals;
  const seoTitle = company?.name
    ? `${companyCode} — ${company.name}`
    : `${companyCode} stock analysis`;
  const seoDesc = compositeScore
    ? `${companyCode} stock analysis: Score ${compositeScore.composite_score || compositeScore.final_score || "?"}/100 ${compositeScore.composite_signal || compositeScore.signal || ""}, PE ${compositeScore.pe_ratio || "?"}, Yield ${compositeScore.dividend_yield || "?"}%. Full fundamental analysis and AI insights.`
    : `${companyCode} (${company?.name || "CSE listed"}) — fundamentals, dividends, and AI insights from CSE Investor.`;

  return (
    <div className="space-y-6" data-testid="stock-detail-page">
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        keywords={`${companyCode}, ${company?.name || ""}, CSE, Sri Lanka, Colombo Stock Exchange, ${company?.sector || ""}`}
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          // Phase 4 (Item 17) + Phase 9 Fix 5 — Smart back routing:
          // - "Monthly Investment" → /portfolio?openMonthly=1 (re-opens dialog)
          // - "Portfolio Suggestion" → /portfolio?view=suggestion (preserves
          //   checkbox state via sessionStorage in BulkPurchaseFlow)
          // - everything else → plain history back so we keep scroll/state.
          if (backLabel === "Monthly Investment") {
            navigate("/portfolio?openMonthly=1");
          } else if (backLabel === "Portfolio Suggestion") {
            navigate("/portfolio?view=suggestion");
          } else {
            navigate(-1);
          }
        }}
        className="text-muted-foreground -ml-2 hover:text-foreground"
        data-testid="stock-detail-back"
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to {backLabel}
      </Button>

      {/* Header card */}
      <Card className="border bg-card">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-display text-2xl font-semibold">{company.name}</h1>
                <Badge variant="outline" className="font-mono">{company.company_code}</Badge>
                <Badge variant="secondary">{company.sector}</Badge>
                {/* Test 6 Fix 2 — Sector verdict pill in header. Always
                    rendered when we have a verdict; falls back gracefully
                    when Layer 6 hasn't run yet for the sector. */}
                {compositeScore?.layer6_verdict && (
                  <SectorVerdictPill
                    verdict={compositeScore.layer6_verdict}
                    size="sm"
                    testId="stock-detail-header-sector-verdict"
                  />
                )}
                {/* Test 6 Fix 2 — Data confidence dot in header. ALWAYS
                    rendered for every stock so users see the data quality
                    signal at a glance. Defaults to "minimal" when missing
                    so the popover still explains what to do (upload reports). */}
                <DataConfidenceDot
                  level={compositeScore?.data_confidence || "minimal"}
                  annualReports={compositeScore?.annual_reports_analysed || 0}
                  interimReports={compositeScore?.interim_reports_analysed || 0}
                  hasTradingViewFundamentals={
                    compositeScore?.has_tradingview_fundamentals
                    ?? (compositeScore?.pe_ratio != null || compositeScore?.eps != null || compositeScore?.dividend_yield != null)
                  }
                  lastUpdated={compositeScore?.computed_at || compositeScore?.updated_at}
                  companyCode={company.company_code}
                  testId="stock-detail-header-confidence"
                />
                <FundamentalsSourceBadge
                  source={compositeScore?.fundamentals_data_source}
                  annualYear={compositeScore?.fundamentals_annual_year}
                  corporateActionAdjusted={compositeScore?.corporate_action_adjusted}
                  testId="stock-detail-header-source-badge"
                />
                {(() => {
                  // Stale-AR notice: if the score uses annual-report
                  // fundamentals AND the financial year ended >6 months
                  // ago, warn that any corporate action since then may
                  // not be reflected and direct admins to the Data
                  // Quality page to apply a manual override.
                  const src = compositeScore?.fundamentals_data_source;
                  const yr = parseInt(compositeScore?.fundamentals_annual_year || "0", 10);
                  if (!yr || !(src === "annual_report" || src === "annual_report+csepal")) return null;
                  const fyEnd = new Date(yr, 11, 31); // assume Dec 31 of FY
                  const monthsOld = (Date.now() - fyEnd.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
                  if (monthsOld < 6) return null;
                  return (
                    <span
                      data-testid="stock-detail-stale-ar-notice"
                      className="inline-flex items-center gap-1 rounded-md border border-amber-600/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[11px] leading-tight"
                      title={`Annual report FY ${yr} is ${Math.round(monthsOld)} months old. Any corporate action (rights issue, bonus, split) since then may need a manual override on /admin/data-quality.`}
                    >
                      AR &gt; 6mo
                    </span>
                  );
                })()}
                {activeSec && <WatchlistStarButton cseSymbol={activeSec.security.cse_symbol} companyCode={company.company_code} shareType={activeSec.security.share_type} />}
                {activeSec?.security?.cse_symbol && (
                  <Link
                    to={`/compare?stocks=${activeSec.security.cse_symbol}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 hover:bg-primary/10 px-2.5 py-1 text-xs text-primary font-medium transition-colors"
                    data-testid="stock-detail-compare-link"
                  >
                    <GitCompare className="h-3 w-3" />
                    Compare
                  </Link>
                )}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{company.description || "Listed on the Colombo Stock Exchange."}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {bundle.securities.map((s) => (
                <button
                  key={s.security.cse_symbol}
                  onClick={() => setActiveSymbol(s.security.cse_symbol)}
                  className={`rounded-md border px-3 py-1.5 text-xs ${activeSymbol === s.security.cse_symbol ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "border-border hover:bg-muted"}`}
                  data-testid={`security-pill-${s.security.cse_symbol}`}
                >
                  <span className="font-mono">{s.security.cse_symbol}</span>
                  <span className="ml-2"><ShareTypePill shareType={s.security.share_type} /></span>
                </button>
              ))}
            </div>
          </div>
          {activeSec?.latest_price && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <Stat label="Last close" value={formatLKR(activeSec.latest_price.close_price)} />
              <Stat label="Day range" value={`${formatLKR(activeSec.latest_price.low_price)} – ${formatLKR(activeSec.latest_price.high_price)}`} />
              <Stat label="Volume" value={formatNumber(activeSec.latest_price.volume, { compact: true })} />
              <Stat label="As of" value={formatDate(activeSec.latest_price.date)} />
            </div>
          )}
          {/* 2026-05-09 — Corporate-action data quality warning banner.
              Rendered only when the merge flagged this stock as a likely
              split/rights/bonus and neither AR nor TV had a safer EPS.
              Backend supplies the full message pre-formatted via
              `composite_scores.data_quality_warning`. */}
          {compositeScore?.data_quality_warning && (
            <div
              role="alert"
              data-testid="stock-detail-corporate-action-warning"
              className="mt-5 rounded-lg border-2 border-amber-500/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-100"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                <div className="flex-1">
                  <div className="font-semibold mb-1">
                    Data Quality Warning
                  </div>
                  <p className="leading-relaxed">
                    {compositeScore.data_quality_warning}
                  </p>
                  {compositeScore?.eps_audit && (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
                      {compositeScore.eps_audit.ar_eps != null && (
                        <span>AR EPS: <b>{Number(compositeScore.eps_audit.ar_eps).toFixed(2)}</b></span>
                      )}
                      {compositeScore.eps_audit.ar_pe != null && (
                        <span>AR PE: <b>{Number(compositeScore.eps_audit.ar_pe).toFixed(2)}x</b></span>
                      )}
                      {compositeScore.eps_audit.tv_eps != null && (
                        <span>TV EPS: <b>{Number(compositeScore.eps_audit.tv_eps).toFixed(2)}</b></span>
                      )}
                      {compositeScore.eps_audit.tv_pe != null && (
                        <span>TV PE: <b>{Number(compositeScore.eps_audit.tv_pe).toFixed(2)}x</b></span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="mt-3">
            <DataSourceNote testId="cse-detail-data-note" />
          </div>
          {/* Feature 2 — Broker-style Research Report download button.
              Uses a hidden anchor so the browser triggers a native file
              download with the correct Content-Disposition filename. */}
          {activeSec?.security?.cse_symbol && (
            <div className="mt-5 pt-4 border-t flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="gap-2 border-primary/30 hover:bg-primary/5"
                onClick={() => downloadResearchReport(activeSec.security.cse_symbol, company.name)}
                disabled={downloadingPdf}
                data-testid="stock-detail-download-research-pdf"
                title="Generate a 6-page broker-style research report (PDF)"
              >
                {downloadingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                {downloadingPdf ? "Generating PDF…" : "Download Research Report"}
              </Button>
              <span className="text-[11px] text-muted-foreground self-center">
                6-page PDF · cover, summary, financials, valuation, technical, disclaimer
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="detail-tabs">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl" data-testid="detail-tabs-list">
          <TabsTrigger value="overview" data-testid="detail-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="fundamentals" data-testid="detail-tab-fundamentals">Fundamentals</TabsTrigger>
          <TabsTrigger value="prices" data-testid="detail-tab-prices">Price History</TabsTrigger>
          <TabsTrigger value="news" data-testid="detail-tab-news">News</TabsTrigger>
          <TabsTrigger value="reports" data-testid="detail-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="ask" data-testid="detail-tab-ask">Ask AI</TabsTrigger>
        </TabsList>

        {/* TAB 1: OVERVIEW */}
        <TabsContent value="overview" className="mt-4 focus-visible:outline-none space-y-6">
          {/* Indicator 4 — small banner on Overview when a report for
              this company is currently being analysed by Claude right
              now. Matches the strict filter used by
              ReportAnalysisBanner (status === "analysing" only). */}
          {(() => {
            const analysingCount = (reports || []).filter(
              (r) => (r.analysis_status || "").toLowerCase() === "analysing",
            ).length;
            if (analysingCount === 0) return null;
            return (
              <div
                className="rounded-md border border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/5 px-3 py-2 flex items-start gap-2 text-xs"
                data-testid="overview-report-analysing-indicator"
              >
                <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--info))] animate-pulse" />
                <span>
                  <span className="font-medium text-foreground">
                    Report analysis in progress…
                  </span>{" "}
                  <span className="text-muted-foreground">
                    New insights for {companyCode} will appear shortly.
                  </span>
                </span>
              </div>
            );
          })()}
          {/* About / Company description — top of Overview, ABOVE the score breakdown */}
          {(() => {
            const cmp = bundle?.company || {};
            // Priority order:
            //  1. Persisted Claude-generated description on the company doc
            //  2. AI investment rationale's first 2-3 sentences (per user spec, Option A)
            //  3. ai_analysis.company_summary fallback
            const persisted = cmp.company_description;
            const rationale = aiForActive?.rationale || bundle?.ai_analysis?.company_summary;
            const summarise = (txt) => {
              if (!txt) return null;
              const sents = String(txt)
                .replace(/\s+/g, " ")
                .match(/[^.!?]+[.!?]+/g) || [String(txt)];
              return sents.slice(0, 3).join(" ").trim();
            };
            const desc = persisted || summarise(rationale);
            const sector = cmp.sector || activeSec?.fund?.sector;
            const mcap = activeSec?.fund?.market_cap_lkr_mn || activeSec?.security?.market_cap_lkr_mn;
            const fmtLkrMn = (v) => {
              if (v == null) return null;
              if (v >= 1000) return `Rs. ${(v / 1000).toFixed(2)} bn`;
              return `Rs. ${Number(v).toFixed(0)} mn`;
            };
            return (
              <Card className="border bg-card" data-testid="company-about-card">
                <CardHeader className="pb-2">
                  <CardTitle className="font-display text-base flex items-center gap-2">
                    <Info className="h-4 w-4 text-primary" />
                    About {bundle?.company?.company_name || activeSec?.security?.company_name || "this company"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm leading-relaxed text-foreground/90" data-testid="company-about-text">
                    {desc || (
                      <span className="italic text-muted-foreground">
                        Description not yet generated for this company. Use the “Ask AI”
                        tab below to ask Claude what this business does — it will respond
                        with the latest available information.
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap text-[11.5px] text-muted-foreground">
                    <span>Listed on CSE</span>
                    {sector && <span>·</span>}
                    {sector && <span>{sector}</span>}
                    {mcap != null && <span>·</span>}
                    {mcap != null && <span>Market cap: {fmtLkrMn(mcap)}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Phase 6 Stage 3: Five-Layer composite score breakdown */}
          {isAdmin && (
            <div
              className="flex items-center justify-between gap-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-2"
              data-testid="admin-recalculate-score-bar"
            >
              <div className="min-w-0">
                <div className="text-xs font-medium text-foreground flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Admin tools
                </div>
                <div className="text-[11px] text-muted-foreground leading-snug">
                  Instantly recompute this stock's composite score — useful after uploading a new report or updating fundamentals.
                </div>
              </div>
              <Button
                size="sm"
                variant="default"
                onClick={recomputeScore}
                disabled={recomputing}
                className="gap-1.5 shrink-0"
                data-testid="recalculate-score-button"
              >
                {recomputing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCcw className="h-3.5 w-3.5" />
                )}
                {recomputing ? "Recalculating…" : "Recalculate Score"}
              </Button>
            </div>
          )}
          <FiveLayerBreakdownPanel
            score={compositeScore}
            aiOneLiner={aiForActive?.rationale || bundle?.ai_analysis?.company_summary}
          />

          {/* Valuation Status pill — at-a-glance verdict on price vs.
              fair value (target_base_case with Graham fallback). */}
          {compositeScore?.valuation_status && compositeScore.valuation_status !== "no_data" && (
            <Card
              className="border bg-card"
              data-testid="stock-detail-valuation-pill"
            >
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <ValuationBadge
                  status={compositeScore.valuation_status}
                  discountPct={compositeScore.valuation_discount_pct}
                  size="md"
                />
                <div className="text-sm text-muted-foreground leading-tight">
                  {compositeScore.current_price != null && compositeScore.fair_value_used != null && (
                    <>
                      <span className="font-mono tabular-nums text-foreground font-medium">
                        {formatLKR(compositeScore.current_price)}
                      </span>
                      <span className="mx-1">vs fair value</span>
                      <span className="font-mono tabular-nums text-foreground font-medium">
                        {formatLKR(compositeScore.fair_value_used)}
                      </span>
                      {compositeScore.valuation_discount_pct != null && (
                        <span className="ml-2">
                          ·{" "}
                          <span
                            className={`font-medium ${
                              compositeScore.valuation_discount_pct > 0
                                ? "text-[hsl(var(--success))]"
                                : compositeScore.valuation_discount_pct < 0
                                  ? "text-[hsl(var(--destructive))]"
                                  : "text-foreground"
                            }`}
                          >
                            {Math.abs(compositeScore.valuation_discount_pct).toFixed(0)}%{" "}
                            {compositeScore.valuation_discount_pct > 0
                              ? "margin of safety"
                              : compositeScore.valuation_discount_pct < 0
                                ? "premium"
                                : "in line"}
                          </span>
                        </span>
                      )}
                    </>
                  )}
                  {compositeScore.fair_value_basis === "graham" && (
                    <Badge variant="outline" className="ml-2 text-[10px] font-normal">
                      Graham fallback
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Intrinsic Value Estimates — Phase 6 Stage 2.
              Reordered (per UX feedback) to appear BEFORE the Target
              Price card so the natural reading flow is:
                Score → "Why undervalued?" (intrinsic) →
                "What is target?" (target price) → Full deep analysis. */}
          <IntrinsicValueSection score={compositeScore} />

          {/* Phase 8 (Item 26) — Multi-method Target Price card.
              Reads intrinsic value as one of its inputs (Graham number,
              DDM) so it logically comes AFTER intrinsic estimates. */}
          <TargetPriceCard score={compositeScore} />

          {/* Deep Analysis (Phase 5) — 6-specialist research.
              Comes LAST in this section because it is the most
              comprehensive view and references everything above.
              Wrapped in an ErrorBoundary so any runtime / polling
              failure inside the Deep Analysis subtree CANNOT break
              the rest of the Stock Detail page. */}
          <DeepAnalysisErrorBoundary symbol={activeSec?.security?.cse_symbol || activeSymbol}>
            <DeepAnalysisSection
              symbol={activeSec?.security?.cse_symbol || activeSymbol}
              sector={compositeScore?.sector || activeSec?.company?.sector}
            />
          </DeepAnalysisErrorBoundary>

          <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-6">
            {/* Phase 7 Stage 6 — Investment Rationale (replaces legacy AI Analysis card) */}
            <InvestmentRationalePanel
              cseSymbol={activeSec?.security?.cse_symbol || activeSymbol}
              compositeScore={compositeScore}
            />

            <div className="space-y-6">
              {activeSec?.security?.cse_symbol && (
                <TechnicalAnalysisCard cseSymbol={activeSec.security.cse_symbol} />
              )}
              <ScoreHistoryCard rows={scoreHistory} symbol={activeSymbol} />
              {activeSec?.security?.cse_symbol && (
                <MarketContextCard cseSymbol={activeSec.security.cse_symbol} />
              )}
              {bundle.share_comparison && <ShareClassComparisonCard comparison={bundle.share_comparison} />}
              {bundle.dividends.length > 0 && (
                <Card className="border bg-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-display text-base">Recent dividends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Timeline</div>
                    <DividendTimeline items={bundle.dividends.slice(0, 6)} />
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* TAB 2: FUNDAMENTALS */}
        <TabsContent value="fundamentals" className="mt-4 focus-visible:outline-none space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6">
            <Card className="border bg-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="font-display">Fundamentals</CardTitle>
                    {fund?.fundamentals_source === "admin_manual" && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Last edited manually by an admin.
                      </p>
                    )}
                    {fund?.fundamentals_source === "tradingview" && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Sourced from TradingView screener.
                      </p>
                    )}
                  </div>
                  {isAdmin && activeSec?.security?.cse_symbol && (
                    <EditFundamentalsDialog
                      cseSymbol={activeSec.security.cse_symbol}
                      currentFund={fund}
                      onSaved={() => {
                        apiGet(`/companies/${companyCode}/full`).then(setBundle).catch(() => {});
                      }}
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-md border" data-testid="fundamentals-table">
                  <table className="w-full text-sm">
                    <tbody>
                      <FundRow label="P/E Ratio" value={fund?.pe_ratio?.toFixed(2)} />
                      <FundRow label="P/B Ratio" value={fund?.pb_ratio?.toFixed(2)} />
                      <FundRow
                        label="EPS"
                        value={fund?.eps != null ? formatLKR(fund.eps) : null}
                        hint={
                          // Show annualisation note when the merge layer
                          // expanded an interim (Q1/H1/Q3) EPS to a 12m
                          // equivalent. Surface both the reported value and
                          // the period so the user knows what's going on.
                          compositeScore?.eps_period_audit?.annualisation_applied
                            ? `Annualised from ${
                                ({ 3: "Q1 (3 mo)", 6: "H1 (6 mo)", 9: "Q3 (9 mo)" })[
                                  compositeScore.eps_period_audit.period_months
                                ] || `${compositeScore.eps_period_audit.period_months} mo`
                              } reported EPS ${
                                Number(compositeScore.eps_period_audit.eps_reported).toFixed(2)
                              } × ${compositeScore.eps_period_audit.annualisation_factor?.toFixed?.(2) || "n/a"}`
                            : null
                        }
                      />
                      <FundRow label="ROE" value={fund?.roe != null ? formatPct(fund.roe) : null} />
                      <FundRow label="Dividend Yield" value={fund?.dividend_yield != null ? formatPct(fund.dividend_yield) : null} />
                      <FundRow
                        label={
                          fund?.dps_source === "tv_estimated" || (!fund?.dividend_per_share && fund?.estimated_dps)
                            ? "Dividend Per Share (est.)"
                            : "Dividend Per Share (DPS)"
                        }
                        value={
                          fund?.dividend_per_share != null
                            ? formatLKR(fund.dividend_per_share)
                            : (fund?.estimated_dps != null ? `${formatLKR(fund.estimated_dps)} (est.)` : null)
                        }
                      />
                      <FundRow label="Debt to Equity" value={fund?.debt_to_equity?.toFixed(2)} />
                      <FundRow label="Revenue Growth (TTM YoY)" value={fund?.revenue_growth != null ? formatPct(fund.revenue_growth) : null} />
                      <FundRow label="Earnings Growth (TTM YoY)" value={fund?.earnings_growth != null ? formatPct(fund.earnings_growth) : null} />
                      <FundRow label="Market Cap" value={fund?.market_cap != null ? formatLKR(fund.market_cap, { compact: true, decimals: 1 }) : null} />
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Last updated {fund?.updated_at ? formatDateTime(fund.updated_at) : (fund?.created_at ? formatDateTime(fund.created_at) : "—")}
                </p>
              </CardContent>
            </Card>

            {/* Sector Comparison (Issue 8) */}
            <SectorComparisonCard data={sectorCompare} symbol={activeSymbol} />
          </div>

          {/* EPS History (Phase 7 Stage 2 — multi-year + lumpy earnings) */}
          {company?.company_code && (
            <EpsHistoryChart
              companyCode={company.company_code}
              lumpyDetected={!!compositeScore?.layer2_lumpy_earnings}
              medianEps={compositeScore?.layer2_median_eps}
              latestEps={compositeScore?.layer2_latest_eps}
              spikeRatio={compositeScore?.layer2_eps_spike_ratio}
            />
          )}
        </TabsContent>

        {/* TAB 3: PRICE HISTORY */}
        <TabsContent value="prices" className="mt-4 focus-visible:outline-none">
          <Card className="border bg-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="font-display">Price history</CardTitle>
                {prices.length >= 7 && (
                  <Tabs value={range} onValueChange={setRange} data-testid="price-range-tabs">
                    <TabsList>
                      {RANGE_OPTIONS.filter((o) => prices.length >= o.min).map((o) => (
                        <TabsTrigger key={o.value} value={o.value}>{o.label}</TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                )}
                {prices.length > 0 && prices.length < 7 && (
                  <Badge variant="outline" className="text-[10px]" data-testid="price-history-collecting-badge">
                    {prices.length} day{prices.length === 1 ? "" : "s"} collected · range filters unlock at 7+ days
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {prices.length >= 1 && (
                <div style={{ width: "100%", height: 360 }} data-testid="price-history-chart">
                  <ResponsiveContainer key={`${activeSymbol}-${range}-${prices.length}`} width="100%" height={360}>
                    <ComposedChart data={prices} margin={{ left: 6, right: 6, top: 12, bottom: 0 }}>
                      <defs>
                        <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.4} strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                        tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: "short", day: "2-digit" })}
                        minTickGap={32}
                        axisLine={false}
                        tickLine={false}
                      />
                      {/* Price axis — top 80% of plot area */}
                      <YAxis
                        yAxisId="price"
                        tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                        domain={["auto", "auto"]}
                        tickFormatter={(v) => `${Number(v).toFixed(0)}`}
                        width={50}
                        axisLine={false}
                        tickLine={false}
                      />
                      {/* Volume axis — pinned to bottom ~20% via large domain headroom */}
                      <YAxis
                        yAxisId="vol"
                        orientation="right"
                        tick={false}
                        axisLine={false}
                        tickLine={false}
                        width={0}
                        domain={[0, (dataMax) => dataMax * 5]}
                      />
                      <Tooltip
                        cursor={{ stroke: "hsl(var(--chart-1))", strokeWidth: 1, strokeDasharray: "3 3" }}
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 8,
                          fontSize: 12,
                          padding: "8px 10px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))", marginBottom: 4 }}
                        labelFormatter={(v) => formatDate(v)}
                        formatter={(v, key) => key === "volume"
                          ? [formatNumber(v, { compact: true }), "Volume"]
                          : [formatLKR(v), "Close"]}
                      />
                      {/* Volume bars (subtle, behind line) */}
                      <Bar
                        yAxisId="vol"
                        dataKey="volume"
                        fill="hsl(var(--muted-foreground))"
                        opacity={0.18}
                        isAnimationActive={false}
                      />
                      {/* Smooth teal area + line */}
                      <Area
                        yAxisId="price"
                        type="monotone"
                        dataKey="close_price"
                        stroke="hsl(var(--chart-1))"
                        strokeWidth={2}
                        fill="url(#priceGradient)"
                        fillOpacity={1}
                        activeDot={{ r: 4, fill: "hsl(var(--chart-1))", strokeWidth: 0 }}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
              {prices.length === 0 && (
                <div className="rounded-md border border-dashed py-12 text-center text-sm text-muted-foreground" data-testid="price-history-empty">
                  No price history available yet for this security. Daily prices will populate after the next end-of-day collection (Mon–Fri 18:00 SLT).
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: NEWS */}
        <TabsContent value="news" className="mt-4 focus-visible:outline-none">
          <NewsTab companyCode={companyCode} />
        </TabsContent>

        {/* TAB 5: REPORTS */}
        <TabsContent value="reports" className="mt-4 focus-visible:outline-none">
          <div className="space-y-4">
            {/* Indicator 2 — analysis-in-progress banner for this company. */}
            <ReportAnalysisBanner reports={reports} />
            <ReportAnalysisStatusPanel companyCode={companyCode} />
            <Card className="border bg-card" data-testid="reports-tab-card">
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="font-display">Company reports</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">{reports.length} report{reports.length === 1 ? "" : "s"}</Badge>
                  {isAdmin && reports.some((r) => r.is_analysed) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={recomputeScore}
                      disabled={recomputing}
                      className="gap-1.5"
                      data-testid="reports-recalculate-score-button"
                      title="Recompute composite score for this stock using the latest analysed reports"
                    >
                      {recomputing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
                      Recalculate Score
                    </Button>
                  )}
                  {isAdmin && reports.some((r) => (r.analysis_status || "").toLowerCase() === "skipped") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={unskipAllForCompany}
                      disabled={unskipAllBusy || !!bulkBusy}
                      className="gap-1.5 border-[hsl(var(--info))]/40 text-[hsl(var(--info))] hover:bg-[hsl(var(--info))]/10"
                      data-testid="reports-reenable-all-button"
                      title="Flip every skipped row for this stock back to pending so they can be picked up by the next bulk analysis run."
                    >
                      {unskipAllBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      Re-enable All ({reports.filter((r) => (r.analysis_status || "").toLowerCase() === "skipped").length})
                    </Button>
                  )}
                  {isAdmin && (
                    <div className="inline-flex items-center gap-1.5" data-testid="bulk-actions-group">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => runBulkAction("download")}
                        disabled={!!bulkBusy}
                        className="gap-1.5"
                        data-testid="bulk-download-missing-button"
                        title="Fetch newly-published reports from cse.lk without running Claude analysis."
                      >
                        {bulkBusy === "download" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudDownload className="h-3.5 w-3.5" />}
                        Download Missing Only
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => runBulkAction("analyse")}
                        disabled={!!bulkBusy || !reports.some((r) => !r.is_analysed)}
                        className="gap-1.5"
                        data-testid="bulk-analyse-downloaded-button"
                        title="Run Claude on every report already on file but not yet analysed. No new downloads."
                      >
                        {bulkBusy === "analyse" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                        Analyse Downloaded Only
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => runBulkAction("both")}
                        disabled={!!bulkBusy}
                        className="gap-1.5"
                        data-testid="bulk-download-and-analyse-button"
                        title="Fetch new reports from cse.lk AND immediately run Claude on them."
                      >
                        {bulkBusy === "both" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                          <span className="inline-flex items-center gap-0.5">
                            <CloudDownload className="h-3.5 w-3.5" />
                            <Sparkles className="h-3.5 w-3.5" />
                          </span>
                        )}
                        Download & Analyse All
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {isAdmin && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  <span className="font-medium text-foreground/80">Download ≠ Analyse.</span>{" "}
                  Downloading only pulls PDFs from cse.lk (cheap).
                  Analysing runs Claude on each report to extract EPS / revenue / margins (costs tokens).
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {reportsLoading ? (
                <div className="text-sm text-muted-foreground">Loading reports…</div>
              ) : reports.length === 0 ? (
                <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                  No reports uploaded yet for {company.name}.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm" data-testid="reports-list-table">
                    <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Period</th>
                        <th className="px-3 py-2 text-left">Uploaded</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((r) => (
                        <tr key={r.report_id} className="border-t" data-testid={`report-row-${r.report_id}`}>
                          <td className="px-3 py-2 capitalize">{r.report_type || "report"}</td>
                          <td className="px-3 py-2 text-muted-foreground">{r.report_period || "—"}</td>
                          <td className="px-3 py-2 text-muted-foreground">{formatDate(r.created_at || r.uploaded_at)}</td>
                          <td className="px-3 py-2"><ReportStatusBadge report={r} queueInfo={queueMap[r.report_id]} /></td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex items-center gap-1.5">
                              {(() => {
                                const status = (r.analysis_status || "").toLowerCase();
                                const isFailed = status === "failed" || status === "error";
                                const isAnalysing = status === "analysing" || status === "queued";
                                const isSkipped = status === "skipped";
                                const isDiscovered = status === "discovered";
                                const isPending = status === "pending" || !status;
                                const isAnalysed = (status === "complete" || status === "analysed") && !isFailed && !isAnalysing;
                                // Actively-running state: Claude is reading the
                                // PDF right now (or it's on the thread-pool
                                // queue waiting for its turn). Show a spinner
                                // so admins know to come back in a bit.
                                if (isAnalysing) {
                                  return (
                                    <span
                                      className="inline-flex items-center gap-1.5 text-[11px] text-[hsl(var(--info))]"
                                      data-testid={`report-busy-${r.report_id}`}
                                    >
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                      {status === "queued" ? "Queued\u2026" : "Analysing\u2026"}
                                    </span>
                                  );
                                }
                                if (isAnalysed) {
                                  return (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-1.5"
                                        onClick={() => openReportAnalysis(r)}
                                        data-testid={`report-read-${r.report_id}`}
                                        title="View Claude analysis"
                                      >
                                        <Eye className="h-3.5 w-3.5" /> Read Analysis
                                      </Button>
                                      {isAdmin && activeSec?.security?.cse_symbol && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="gap-1.5"
                                          onClick={() => setVerifyReportOpen(true)}
                                          data-testid={`report-verify-${r.report_id}`}
                                          title="Open fundamentals verification dialog to correct extracted figures"
                                        >
                                          <Pencil className="h-3.5 w-3.5" /> Verify Data
                                        </Button>
                                      )}
                                    </>
                                  );
                                }
                                if (isFailed && isAdmin) {
                                  // Fix 1 — admin Retry button. Calls the
                                  // existing /reports/{id}/analyse endpoint
                                  // with force=true so the failed row gets
                                  // a fresh attempt.
                                  return (
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                                      onClick={() => retryReportAnalysis(r)}
                                      disabled={analysingIds.has(r.report_id)}
                                      data-testid={`report-retry-${r.report_id}`}
                                      title={r.analysis_error || "Retry analysis"}
                                    >
                                      <RefreshCcw className="h-3.5 w-3.5" />
                                      {analysingIds.has(r.report_id) ? "Retrying…" : "Retry"}
                                    </Button>
                                  );
                                }
                                // Skipped or Discovered — admin can
                                // re-enable (queue immediately for Claude)
                                // via the unskip endpoint. PDF is on disk,
                                // no re-download required.
                                if ((isSkipped || isDiscovered) && isAdmin) {
                                  return (
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="gap-1.5"
                                      onClick={() => unskipReport(r, "pending")}
                                      disabled={analysingIds.has(r.report_id)}
                                      data-testid={`report-reenable-${r.report_id}`}
                                      title={
                                        isSkipped
                                          ? "Re-queue this report for Claude AI analysis. Uses ~$0.30 of API credits."
                                          : "Send this report to Claude for AI analysis. Uses ~$0.30 of API credits."
                                      }
                                    >
                                      <Sparkles className="h-3.5 w-3.5" />
                                      {analysingIds.has(r.report_id)
                                        ? (isSkipped ? "Re-enabling…" : "Queuing…")
                                        : (isSkipped ? "Re-enable" : "Analyse Now")}
                                    </Button>
                                  );
                                }
                                if (isAdmin) {
                                  return (
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="gap-1.5"
                                      onClick={() => analyseReport(r)}
                                      disabled={analysingIds.has(r.report_id)}
                                      data-testid={`report-analyse-${r.report_id}`}
                                      title="Run Claude analysis now"
                                    >
                                      <Sparkles className="h-3.5 w-3.5" />
                                      {analysingIds.has(r.report_id) ? "Analysing…" : "Analyse Now"}
                                    </Button>
                                  );
                                }
                                return (
                                  <span
                                    className="text-[11px] text-muted-foreground italic"
                                    data-testid={`report-pending-${r.report_id}`}
                                    title="Reports are processed automatically each evening"
                                  >
                                    Analysis pending
                                  </span>
                                );
                              })()}
                              <Button
                                asChild
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                title="Download original PDF"
                              >
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/file/${r.report_id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`report-download-${r.report_id}`}
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              </Button>
                              {/* Fix 5 — Delete button (admin only). Opens
                                  a confirmation dialog before performing
                                  the destructive action. */}
                              {isAdmin && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700"
                                  title="Delete report"
                                  onClick={() => setDeleteTarget({ report_id: r.report_id, title: r.report_title || r.report_period || r.file_name || "Report" })}
                                  data-testid={`report-delete-${r.report_id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Drag & drop upload — ADMIN ONLY (Fix 4 security gate).
                  Regular users see only the read-only reports list above.
                  Uses the unified ReportUploadForm component shared with
                  /reports admin page so both surfaces have identical
                  Type / Year / Month dropdowns + preview-before-upload. */}
              {isAdmin && (
                <ReportUploadForm
                  companyCode={companyCode}
                  companyName={company?.name}
                  onUploaded={() => loadReports()}
                />
              )}
            </CardContent>
          </Card>
          </div>
        </TabsContent>

        {/* TAB 6: ASK AI */}
        <TabsContent value="ask" className="mt-4 focus-visible:outline-none">
          {activeSec?.security?.cse_symbol ? (
            <StockAIChatTab
              symbol={activeSec.security.cse_symbol}
              companyName={activeSec.company?.company_name || activeSec.security?.company_name}
            />
          ) : (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
        </TabsContent>
      </Tabs>

      {/* Controlled fundamentals-verify dialog — triggered by the
          "Verify Data" button on each analysed report row. We mount a
          single instance at page level so it's not re-created for
          every row. */}
      {isAdmin && activeSec?.security?.cse_symbol && (
        <EditFundamentalsDialog
          cseSymbol={activeSec.security.cse_symbol}
          currentFund={fund}
          hideTrigger
          open={verifyReportOpen}
          onOpenChange={setVerifyReportOpen}
          onSaved={() => {
            apiGet(`/companies/${companyCode}/full`).then(setBundle).catch(() => {});
          }}
        />
      )}

      {/* Read Analysis modal — Claude's narrative + extracted fundamentals */}
      <ReportAnalysisDialog
        state={reportAnalysis}
        onClose={closeReportAnalysis}
      />

      {/* Fix 5 — Delete confirmation dialog (admin only) */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-report-dialog">
          <DialogHeader>
            <DialogTitle>Delete this report?</DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <span className="block font-medium text-foreground">{deleteTarget?.title}</span>
              <span className="block text-rose-600 dark:text-rose-400">This cannot be undone — the PDF, extracted analysis, and all linked data will be permanently removed.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="delete-report-cancel">Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="delete-report-confirm">
              {deleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-medium tabular">{value || "—"}</div>
  </div>
);

const FundRow = ({ label, value, editable, onEdit, hint }) => (
  <tr className="border-t first:border-t-0 group">
    <th className="px-4 py-2.5 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium w-1/2">
      {label}
      {hint && (
        <span
          className="ml-1.5 inline-flex items-center text-[10px] text-amber-600 dark:text-amber-400 normal-case font-normal"
          title={hint}
          data-testid={`fundrow-hint-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          ⓘ annualised
        </span>
      )}
    </th>
    <td className="px-4 py-2.5 text-right tabular font-medium">
      {value || <span className="text-muted-foreground/60">—</span>}
      {hint && (
        <div className="text-[10px] text-amber-700 dark:text-amber-300 font-normal mt-0.5">
          {hint}
        </div>
      )}
    </td>
  </tr>
);

const ReportStatusBadge = ({ report, queueInfo }) => {
  // analysis lifecycle: pending -> analysing -> analysed | failed
  // ORDER MATTERS — check the live status FIRST, otherwise a
  // previously-failed row that was just retried (still has
  // is_analysed=True from the prior failure handler) would briefly
  // render the "Analysed" badge until the next poll cycle.
  const status = (report.analysis_status || "").toLowerCase();
  const isFailed = status === "failed" || status === "error";
  const isAnalysing = status === "analysing";
  const isSkipped = status === "skipped";
  const isDiscovered = status === "discovered";
  // Strict definition of "Analysed" — needs `analysis_complete=True`
  // AND status `complete|analysed`. The legacy `is_analysed` boolean
  // alone is unreliable because the failure handler also sets it.
  const isAnalysed = (status === "complete" || status === "analysed") && !isFailed && !isAnalysing;
  if (isFailed) {
    return (
      <Badge variant="outline" className="bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30 text-[10px] gap-1" data-testid="report-status-failed" title={report.analysis_error || ""}>
        Failed
      </Badge>
    );
  }
  if (isAnalysing) {
    return (
      <Badge variant="outline" className="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/30 text-[10px] gap-1" data-testid="report-status-analysing">
        <Loader2 className="h-2.5 w-2.5 animate-spin" /> Analysing
      </Badge>
    );
  }
  if (isAnalysed) {
    return (
      <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 text-[10px] gap-1" data-testid="report-status-analysed">
        Analysed
      </Badge>
    );
  }
  if (isSkipped) {
    // Admin-cancelled queue entry. PDF is still on disk — admin can
    // re-enable via the "Re-enable" button in the actions column.
    return (
      <Badge
        variant="outline"
        className="bg-muted/40 text-muted-foreground border-muted-foreground/30 text-[10px] gap-1"
        data-testid="report-status-skipped"
        title={report.analysis_skip_reason || "Skipped by admin — click Re-enable to queue for Claude."}
      >
        Skipped
      </Badge>
    );
  }
  if (isDiscovered) {
    // Downloaded by the nightly cron but parked rather than auto-queued
    // (cost guard). Admin must promote via "Re-enable" or the bulk
    // "Analyse Downloaded Only" action.
    return (
      <Badge
        variant="outline"
        className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 text-[10px] gap-1"
        data-testid="report-status-discovered"
        title="Downloaded by cron — awaiting admin promotion before Claude analysis."
      >
        <Download className="h-2.5 w-2.5" />
        Discovered
      </Badge>
    );
  }
  // Pending + in admin queue: show explicit queue position so the admin
  // knows roughly when Claude will pick this up. Falls back to the
  // generic "Downloaded · Not analysed" badge when no queue info is
  // available (e.g. non-admin viewer or cleared queue).
  if (queueInfo?.position) {
    const etaMinutes = Math.max(1, Math.round(queueInfo.position * 5));
    const eta =
      etaMinutes < 60 ? `~${etaMinutes} min` : `~${Math.floor(etaMinutes / 60)}h ${etaMinutes % 60}m`;
    return (
      <Badge
        variant="outline"
        className="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/40 text-[10px] gap-1"
        data-testid="report-status-queued"
        title={`Queued — position ${queueInfo.position} of ${queueInfo.queue_length}. Est wait ${eta}.`}
      >
        <Download className="h-2.5 w-2.5" />
        Queued · #{queueInfo.position} of {queueInfo.queue_length}
        {queueInfo.is_prioritised ? " ⭐" : ""}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 text-[10px] gap-1"
      data-testid="report-status-downloaded"
      title="PDF downloaded and stored. Waiting for Claude analysis — either the nightly cron (top-20 annual BUY stocks) or the Analyse Now button."
    >
      <Download className="h-2.5 w-2.5" />
      Downloaded · Not analysed
    </Badge>
  );
};



const SectorComparisonCard = ({ data, symbol }) => {
  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <Card className="border bg-card" data-testid="sector-comparison-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-base">vs Sector</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-dashed px-4 py-6 text-center text-xs text-muted-foreground">
            Sector comparison will appear once more peer fundamentals are available.
          </div>
        </CardContent>
      </Card>
    );
  }
  const fmt = (key, v) => {
    if (v == null) return "—";
    if (key === "dividend_yield" || key === "roe" || key === "revenue_growth" || key === "earnings_growth") {
      return formatPct(v);
    }
    if (key === "eps") return formatLKR(v);
    return Number(v).toFixed(2);
  };
  const toneCls = {
    favourable: "text-[hsl(var(--success))]",
    unfavourable: "text-[hsl(var(--destructive))]",
    neutral: "text-muted-foreground",
  };
  const toneIcon = {
    favourable: <TrendingUp className="h-3.5 w-3.5" />,
    unfavourable: <TrendingDown className="h-3.5 w-3.5" />,
    neutral: <ArrowRight className="h-3.5 w-3.5" />,
  };
  return (
    <Card className="border bg-card" data-testid="sector-comparison-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="font-display text-base">vs Sector</CardTitle>
          <Badge variant="outline" className="text-[10px]">
            {data.sector} · {data.constituents_count} peer{data.constituents_count === 1 ? "" : "s"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Metric</th>
                <th className="px-3 py-2 text-right">{symbol}</th>
                <th className="px-3 py-2 text-right">Sector median</th>
                <th className="px-3 py-2 text-right">vs Sector</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.key} className="border-t" data-testid={`sector-compare-row-${r.key}`}>
                  <td className="px-3 py-2 text-muted-foreground">{r.label}</td>
                  <td className="px-3 py-2 text-right tabular font-medium">{fmt(r.key, r.value)}</td>
                  <td className="px-3 py-2 text-right tabular text-muted-foreground">{fmt(r.key, r.sector_median)}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-flex items-center gap-1 tabular font-medium ${toneCls[r.tone]}`}>
                      {toneIcon[r.tone]}
                      {r.delta_pct == null ? "—" : `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct.toFixed(1)}%`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[10px] text-muted-foreground">
          Sector medians are recomputed nightly across {data.constituents_count} peer companies. ✅ favourable · ❌ unfavourable · ➡ within ±10% of sector.
        </p>
      </CardContent>
    </Card>
  );
};

const PriceHistoryEmpty = ({ firstDate, latest, count = 0 }) => (
  <div
    className="rounded-md border border-dashed bg-muted/10 px-6 py-10 text-center"
    data-testid="price-history-empty"
  >
    <LineChartIcon className="mx-auto h-8 w-8 text-muted-foreground/60" />
    <p className="mt-3 text-sm font-medium">
      Price history collection started {firstDate ? formatDate(firstDate) : "recently"}.
    </p>
    <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto">
      The chart will fill in as daily prices are collected each weekday at 17:30 Sri Lanka time.
      {count > 0 && (
        <> So far we have <span className="font-medium text-foreground/80">{count} day{count === 1 ? "" : "s"}</span> of data — the chart needs at least 7.</>
      )}
      {latest?.close_price != null && (
        <> Latest close on file: <span className="font-medium text-foreground/80">{formatLKR(latest.close_price)}</span>.</>
      )}
    </p>
  </div>
);

const ReportAnalysisDialog = ({ state, onClose }) => {
  // Phase 6 Fix 1 — delegate to the dedicated modal component which handles
  // the 5-section layout with fixed header + scrollable body + key-metrics chips.
  // Kept this thin wrapper so existing call sites don't need to change.
  return <ReportAnalysisModal state={state} onClose={onClose} />;
};

const ScoreHistoryCard = ({ rows, symbol }) => {
  if (!rows || rows.length === 0) {
    return (
      <Card className="border bg-card" data-testid="score-history-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <LineChartIcon className="h-4 w-4 text-primary" /> Score trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-dashed px-4 py-6 text-center text-xs text-muted-foreground">
            Score history will appear once the weekly AI run records its first datapoint.
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = rows.map((r) => ({
    date: r.computed_at,
    score: typeof r.score === "number" ? r.score : null,
  }));
  const latest = chartData[chartData.length - 1];
  const first = chartData[0];
  const delta =
    latest && first && typeof latest.score === "number" && typeof first.score === "number"
      ? latest.score - first.score
      : null;
  const deltaSign = delta == null ? "" : delta > 0 ? "+" : "";
  const deltaTone =
    delta == null
      ? "text-muted-foreground"
      : delta > 0
      ? "text-[hsl(var(--success))]"
      : delta < 0
      ? "text-[hsl(var(--destructive))]"
      : "text-muted-foreground";

  return (
    <Card className="border bg-card" data-testid="score-history-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <LineChartIcon className="h-4 w-4 text-primary" /> Score trend
          </CardTitle>
          <div className="text-xs flex items-center gap-2">
            <span className="text-muted-foreground">{rows.length} pts</span>
            {delta != null && (
              <span className={`font-medium tabular ${deltaTone}`}>
                {deltaSign}{delta.toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ width: "100%", height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: "short", day: "2-digit" })}
                minTickGap={28}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                width={28}
              />
              <ReferenceLine y={50} stroke="hsl(var(--border))" strokeDasharray="2 2" />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                labelFormatter={(v) => formatDate(v)}
                formatter={(v) => [`${v}`, `${symbol || ""} score`]}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="hsl(var(--chart-1))"
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">
          Composite score (0–100). Recorded after every AI re-score / weekly fundamentals refresh.
        </p>
      </CardContent>
    </Card>
  );
};

const BulletList = ({ title, items, tone }) => (
  <div className={`rounded-lg border p-3 ${tone === "success" ? "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/8" : "border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5"}`}>
    <div className={`text-xs uppercase tracking-wide font-medium mb-1.5 ${tone === "success" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>{title}</div>
    <ul className="space-y-1 text-sm">
      {(items || []).map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${tone === "success" ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--destructive))]"}`} />
          <span>{it}</span>
        </li>
      ))}
      {(!items || items.length === 0) && <li className="text-xs text-muted-foreground">—</li>}
    </ul>
  </div>
);

export default StockDetailPage;
